import { useEffect, useState } from 'react';
import historicalMapsData from '../data/historical-maps.json';
import type { HistoricalMap } from '../types/historical-map';
import { ZoomableImage } from './ZoomableImage';
import { DeepZoomViewer } from './DeepZoomViewer';

interface Catalogue {
  version: string;
  maps: HistoricalMap[];
}

const catalogue = historicalMapsData as Catalogue;

interface HistoricalMapsGalleryProps {
  open: boolean;
  onClose: () => void;
}

export function HistoricalMapsGallery({ open, onClose }: HistoricalMapsGalleryProps) {
  const [zoomedId, setZoomedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (zoomedId) setZoomedId(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, zoomedId, onClose]);

  if (!open) return null;

  const zoomedMap = zoomedId ? catalogue.maps.find((m) => m.id === zoomedId) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="historical-maps-gallery"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 'var(--space-6) var(--space-4)',
        overflowY: 'auto',
        fontFamily: 'var(--font-body)',
      }}
      onClick={(e) => {
        // Click backdrop to close (but not when a card is zoomed).
        if (e.target === e.currentTarget && !zoomedId) onClose();
      }}
    >
      <div
        style={{
          width: 'min(1100px, 100%)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-elevated)',
          padding: 'var(--space-5)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'var(--text-headline)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Historical maps of Antwerp
            </h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Pictorial city portraits, engraved plans and cadastral surveys.
              Click any card to view full-resolution in browser.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              padding: '4px 10px',
              fontSize: '1.1rem',
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          {catalogue.maps.map((m) => (
            <MapCard key={m.id} map={m} onClick={() => setZoomedId(m.id)} />
          ))}
        </div>
      </div>

      {zoomedMap ? <ZoomedMap map={zoomedMap} onClose={() => setZoomedId(null)} /> : null}
    </div>
  );
}

function MapCard({ map, onClick }: { map: HistoricalMap; onClick: () => void }) {
  const [imgFailed, setImgFailed] = useState(false);
  const available = map.status === 'available' && !imgFailed;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: 0,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          aspectRatio: '4 / 3',
          background: 'var(--background)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid var(--border)',
          color: 'var(--text-muted)',
          fontSize: '0.75rem',
          textAlign: 'center',
          padding: 'var(--space-2)',
        }}
      >
        {available ? (
          <img
            src={map.thumbnailUrl ?? map.imageUrl}
            alt={map.title}
            loading="lazy"
            onError={() => setImgFailed(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span>
            Scan not yet local.
            <br />
            See archive reference below.
          </span>
        )}
      </div>
      <div style={{ padding: 'var(--space-2) var(--space-3)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-2)',
            marginBottom: 4,
          }}
        >
          <strong
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '0.95rem',
              color: 'var(--text-headline)',
              lineHeight: 1.2,
            }}
          >
            {map.title}
          </strong>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            fontSize: '0.7rem',
            color: 'var(--text-muted)',
            marginBottom: 4,
          }}
        >
          <span>{map.year}</span>
          {map.author ? <span>· {map.author}</span> : null}
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text)', lineHeight: 1.4, marginBottom: 6 }}>
          {map.blurb}
        </p>
        <p
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
            marginBottom: 4,
          }}
        >
          {map.source}
        </p>
        {map.archiveRef ? (
          <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
            {map.archiveRef}
          </p>
        ) : null}
      </div>
    </button>
  );
}

function ZoomedMap({ map, onClose }: { map: HistoricalMap; onClose: () => void }) {
  const hasUpscale = Boolean(map.upscaledImageUrl || map.upscaledDziUrl);
  const [useUpscale, setUseUpscale] = useState<boolean>(() => {
    if (!hasUpscale) return false;
    if (typeof window === 'undefined') return true;
    const saved = window.localStorage.getItem(`map-upscale:${map.id}`);
    return saved === null ? true : saved === 'on';
  });
  const activeImageUrl = useUpscale && map.upscaledImageUrl ? map.upscaledImageUrl : map.imageUrl;
  const activeDziUrl = useUpscale ? map.upscaledDziUrl : map.dziUrl;

  function toggleUpscale() {
    setUseUpscale((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(`map-upscale:${map.id}`, next ? 'on' : 'off');
      }
      return next;
    });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-3)',
          alignItems: 'center',
          color: '#fffdf2',
          fontFamily: 'var(--font-body)',
          flexWrap: 'wrap',
        }}
      >
        <strong style={{ fontFamily: 'var(--font-serif)', fontSize: '1.1rem' }}>
          {map.title}
        </strong>
        <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
          {map.year}
          {map.author ? ` · ${map.author}` : ''}
        </span>
        {hasUpscale ? (
          <button
            type="button"
            onClick={toggleUpscale}
            aria-pressed={useUpscale}
            title="Toggle between original scan and 2× AI-upscaled version (Topaz)"
            data-testid="upscale-toggle"
            style={{
              padding: '4px 10px',
              fontSize: '0.8rem',
              fontWeight: 600,
              background: useUpscale ? '#d4793a' : 'transparent',
              color: useUpscale ? '#1a1410' : '#fffdf2',
              border: '1px solid #d4793a',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {useUpscale ? '2× upscaled' : 'Original'}
          </button>
        ) : null}
        {map.status === 'available' ? (
          <a
            href={activeImageUrl}
            download
            style={{
              padding: '4px 10px',
              fontSize: '0.8rem',
              fontWeight: 600,
              background: '#fffdf2',
              color: '#1a1410',
              borderRadius: 'var(--radius-sm)',
              textDecoration: 'none',
            }}
          >
            Download
          </a>
        ) : map.catalogueUrl ? (
          <a
            href={map.catalogueUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '4px 10px',
              fontSize: '0.8rem',
              fontWeight: 600,
              background: 'transparent',
              color: '#fffdf2',
              border: '1px solid #fffdf2',
              borderRadius: 'var(--radius-sm)',
              textDecoration: 'none',
            }}
          >
            Open in archive →
          </a>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close zoom"
          style={{
            padding: '4px 10px',
            fontSize: '0.9rem',
            background: 'transparent',
            color: '#fffdf2',
            border: '1px solid #fffdf2',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          Close (Esc)
        </button>
      </div>

      {map.status === 'available' ? (
        <div
          style={{
            width: 'min(100vw - var(--space-6) * 2, 1800px)',
            height: 'calc(100vh - 140px)',
            maxWidth: '100%',
          }}
        >
          {activeDziUrl ? (
            <DeepZoomViewer dziUrl={activeDziUrl} alt={map.title} key={activeDziUrl} />
          ) : (
            <ZoomableImage
              src={activeImageUrl}
              alt={map.title}
              maxZoom={useUpscale ? 24 : 16}
              resetSignal={activeImageUrl}
            />
          )}
        </div>
      ) : (
        <div
          style={{
            maxWidth: 520,
            color: '#fffdf2',
            fontFamily: 'var(--font-body)',
            textAlign: 'center',
            lineHeight: 1.55,
          }}
        >
          <p style={{ fontSize: '0.95rem', marginBottom: 'var(--space-3)' }}>
            This map exists in the public collections of <strong>{map.source}</strong>.
            We haven't dropped a publication-grade scan into MapLens yet.
          </p>
          {map.archiveRef ? (
            <p style={{ fontSize: '0.85rem', fontFamily: 'monospace', marginBottom: 'var(--space-3)' }}>
              {map.archiveRef}
            </p>
          ) : null}
          {map.catalogueUrl ? (
            <a
              href={map.catalogueUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '8px 14px',
                fontSize: '0.85rem',
                fontWeight: 600,
                background: '#fffdf2',
                color: '#1a1410',
                borderRadius: 'var(--radius-sm)',
                textDecoration: 'none',
              }}
            >
              Open the archive record →
            </a>
          ) : null}
        </div>
      )}
    </div>
  );
}
